import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import UIEventBus from '../EventBus';

type DeskItemKind =
    | 'resume'
    | 'note-tip'
    | 'note-motto'
    | 'note-contact';

interface HoverState {
    label: string;
    x: number;
    y: number;
}

interface ToastState {
    id: number;
    text: string;
}

const EMAIL = 'sencersenyurt@gmail.com';
const LINKEDIN_URL = 'https://www.linkedin.com/in/irfansenyurt/';
const GITHUB_URL = 'https://github.com/sncrsenyurt';
const RESUME_PDF = 'resume/irfansenyurt-resume.pdf';

const QA_TIPS = [
    'A bug report without repro steps is just a rumor.',
    'If it only fails on Fridays, it is still a bug.',
    'Automate the boring checks so humans can explore the weird ones.',
    '"Works on my machine" is a data point, not a verdict.',
    'Flaky test? Fix it or quarantine it. Never just hit re-run.',
    'Test the unhappy path first. Users will find it anyway.',
];

const DEV_MOTTOS = [
    {
        quote: 'Talk is cheap. Show me the code.',
        attrib: 'Linus Torvalds',
    },
    {
        quote: 'First, make it work. Then make it right. Then make it fast.',
        attrib: 'Kent Beck',
    },
    {
        quote: 'Programs must be written for people to read, and only incidentally for machines to execute.',
        attrib: 'Harold Abelson',
    },
    {
        quote: 'Premature optimization is the root of all evil.',
        attrib: 'Donald Knuth',
    },
    {
        quote: 'Simplicity is prerequisite for reliability.',
        attrib: 'Edsger Dijkstra',
    },
    {
        quote: 'Any fool can write code that a computer can understand. Good programmers write code that humans can understand.',
        attrib: 'Martin Fowler',
    },
    {
        quote: 'Fix the cause, not the symptom.',
        attrib: 'Steve Maguire',
    },
    {
        quote: 'The best performance improvement is the transition from the nonworking state to the working state.',
        attrib: 'John Ousterhout',
    },
];

const NOTE_COLORS: { [key in DeskItemKind]: string } = {
    resume: '#e4e2dc',
    'note-tip': '#f6e27f',
    'note-motto': '#f6a6c1',
    'note-contact': '#9fe3c4',
};

/**
 * HTML layer for the 3D desk props: hover tooltip, info panel and toasts.
 * Rendered into #desk-overlay. Hitboxes.ts swallows mousedown events inside
 * this element so the camera does not react to clicks on the panel.
 */
const DeskOverlay: React.FC = () => {
    const [item, setItem] = useState<DeskItemKind | null>(null);
    const [hover, setHover] = useState<HoverState | null>(null);
    const [toast, setToast] = useState<ToastState | null>(null);
    const [tipIndex, setTipIndex] = useState(0);
    const [mottoIndex, setMottoIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        UIEventBus.on('deskItemOpen', (data: { kind: DeskItemKind }) => {
            setHover(null);
            setItem(data.kind);
            UIEventBus.dispatch('deskOverlayOpen', {});
        });
        UIEventBus.on('deskHover', (data: HoverState | null) => {
            setHover(data);
        });
        UIEventBus.on('deskToast', (data: { text: string }) => {
            setToast({ id: Date.now(), text: data.text });
        });
        // Hide the tooltip when the cursor enters the monitor
        UIEventBus.on('enterMonitor', () => setHover(null));
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!toast) return;
        const timeout = setTimeout(() => setToast(null), 2800);
        return () => clearTimeout(timeout);
    }, [toast]);

    const close = useCallback(() => {
        setItem(null);
        UIEventBus.dispatch('deskOverlayClosed', {});
        UIEventBus.dispatch('deskItemClose', {});
    }, []);

    useEffect(() => {
        if (!item) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [item, close]);

    const renderContent = (kind: DeskItemKind) => {
        switch (kind) {
            case 'resume':
                return (
                    <>
                        <h3 style={styles.title}>Resume</h3>
                        <p style={styles.subtitle}>
                            Software QA Engineer · Tesla
                        </p>
                        <p style={styles.body}>
                            Software QA Engineer at Tesla leading a 7-person
                            team that tests every major in-vehicle software
                            release. Builds AI-driven automation integrated
                            with Jenkins, plus internal tools and test
                            automation in Python, JavaScript, and Ruby.
                            Earlier experience includes autonomous vehicle
                            testing at Zoox and quality management systems at
                            Autoliv Türkiye.
                        </p>
                        <div style={styles.actionsColumn}>
                            <a
                                href={RESUME_PDF}
                                target="_blank"
                                rel="noreferrer"
                                style={styles.button}
                            >
                                Open full PDF ↗
                            </a>
                            <a
                                href={RESUME_PDF}
                                download="irfansenyurt-resume.pdf"
                                style={styles.button}
                            >
                                ⬇ Download resume
                            </a>
                        </div>
                    </>
                );
            case 'note-tip':
                return (
                    <>
                        <h3 style={styles.title}>QA tip #{tipIndex + 1}</h3>
                        <p style={{ ...styles.body, ...styles.quote }}>
                            {QA_TIPS[tipIndex]}
                        </p>
                        <div style={styles.actions}>
                            <button
                                style={styles.button}
                                onClick={() =>
                                    setTipIndex((i) => (i + 1) % QA_TIPS.length)
                                }
                            >
                                Next tip →
                            </button>
                        </div>
                    </>
                );
            case 'note-motto':
                return (
                    <>
                        <h3 style={styles.title}>
                            Dev motto #{mottoIndex + 1}
                        </h3>
                        <p style={{ ...styles.body, ...styles.quote }}>
                            "{DEV_MOTTOS[mottoIndex].quote}"
                        </p>
                        <p style={styles.subtitle}>
                            — {DEV_MOTTOS[mottoIndex].attrib}
                        </p>
                        <div style={styles.actions}>
                            <button
                                style={styles.button}
                                onClick={() =>
                                    setMottoIndex(
                                        (i) => (i + 1) % DEV_MOTTOS.length
                                    )
                                }
                            >
                                Next motto →
                            </button>
                        </div>
                    </>
                );
            case 'note-contact':
                return (
                    <>
                        <h3 style={styles.title}>Say hi!</h3>
                        <p style={styles.body}>
                            Open to chatting about QA, test automation and
                            tooling.
                        </p>
                        <div style={styles.actionsColumn}>
                            <a href={`mailto:${EMAIL}`} style={styles.button}>
                                ✉ {EMAIL}
                            </a>
                            <a
                                href={LINKEDIN_URL}
                                target="_blank"
                                rel="noreferrer"
                                style={styles.button}
                            >
                                LinkedIn ↗
                            </a>
                            <a
                                href={GITHUB_URL}
                                target="_blank"
                                rel="noreferrer"
                                style={styles.button}
                            >
                                GitHub ↗
                            </a>
                        </div>
                    </>
                );
        }
    };

    return (
        <div
            style={{
                ...styles.root,
                pointerEvents: item ? 'auto' : 'none',
            }}
        >
            {/* Hover tooltip */}
            {hover && !item && (
                <div
                    style={{
                        ...styles.tooltip,
                        left: hover.x + 16,
                        top: hover.y + 16,
                    }}
                >
                    {hover.label}
                </div>
            )}

            {/* Info panel */}
            <AnimatePresence>
                {item && (
                    <motion.div
                        key="backdrop"
                        style={styles.backdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={close}
                    />
                )}
                {item && (
                    <motion.div
                        key={'panel-' + item}
                        style={{
                            ...styles.panel,
                            ...(isMobile ? styles.panelMobile : {}),
                            borderTop: `6px solid ${NOTE_COLORS[item]}`,
                        }}
                        initial={{ opacity: 0, x: isMobile ? 0 : 40, y: isMobile ? 40 : 0 }}
                        animate={{ opacity: 1, x: 0, y: 0 }}
                        exit={{ opacity: 0, x: isMobile ? 0 : 40, y: isMobile ? 40 : 0 }}
                        transition={{ duration: 0.35, delay: 0.25, ease: 'easeOut' }}
                    >
                        <button
                            style={styles.close}
                            onClick={close}
                            aria-label="Close"
                        >
                            [ X ]
                        </button>
                        {renderContent(item)}
                        <p style={styles.hint}>Esc / click outside to go back</p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toast */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        key={toast.id}
                        style={styles.toast}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                    >
                        {toast.text}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const styles: StyleSheetCSS = {
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
        fontFamily: 'monospace',
    },
    tooltip: {
        position: 'fixed',
        pointerEvents: 'none',
        background: 'black',
        color: 'white',
        padding: '4px 8px',
        fontSize: 12,
        fontFamily: 'monospace',
        whiteSpace: 'nowrap',
    },
    backdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.18)',
    },
    panel: {
        position: 'absolute',
        top: '50%',
        right: 64,
        marginTop: -260,
        width: 360,
        maxHeight: 'calc(100% - 64px)',
        overflowY: 'auto',
        boxSizing: 'border-box',
        background: 'black',
        color: 'white',
        padding: 24,
        textAlign: 'left',
        boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
    },
    panelMobile: {
        top: 'auto',
        bottom: 0,
        right: 0,
        left: 0,
        marginTop: 0,
        width: '100%',
        maxHeight: '70%',
        padding: 18,
    },
    close: {
        position: 'absolute',
        top: 10,
        right: 10,
        background: 'transparent',
        border: 'none',
        color: 'white',
        fontFamily: 'monospace',
        fontSize: 14,
        cursor: 'pointer',
    },
    title: {
        fontSize: 20,
        marginBottom: 6,
        marginTop: 8,
    },
    subtitle: {
        fontSize: 13,
        opacity: 0.75,
        marginBottom: 14,
    },
    body: {
        fontSize: 14,
        lineHeight: 1.5,
        marginBottom: 12,
    },
    quote: {
        fontSize: 17,
        fontStyle: 'italic',
    },
    actions: {
        display: 'flex',
        gap: 8,
        marginTop: 8,
    },
    actionsColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 8,
    },
    button: {
        display: 'inline-block',
        background: 'white',
        color: 'black',
        border: 'none',
        padding: '8px 12px',
        fontFamily: 'monospace',
        fontSize: 13,
        textDecoration: 'none',
        cursor: 'pointer',
    },
    hint: {
        fontSize: 11,
        opacity: 0.5,
        marginTop: 16,
    },
    toast: {
        position: 'absolute',
        bottom: 48,
        left: '50%',
        marginLeft: -170,
        width: 340,
        textAlign: 'center',
        background: 'black',
        color: 'white',
        padding: '10px 14px',
        fontFamily: 'monospace',
        fontSize: 14,
        pointerEvents: 'none',
        boxSizing: 'border-box',
    },
};

export default DeskOverlay;
